import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CardChrome } from './_CardChrome';
import { Input } from '../../components/common/Input';
import { Button } from '../../components/common/Button';
import { resetPassword, ExternalApiError } from '../../services/api/externalAuth';
import { passwordSetSchema, type PasswordSetInput } from './_schemas';

/**
 * Reset-password page.
 *
 * Token contract: a `?token=` query string is required. Without one we render
 * a static "Link unavailable" page (no form). With one, we POST it alongside
 * the new password to /api/external/reset-password.
 *
 * On success we navigate to /external/login with `state.message`, which
 * LoginPage renders as a green banner. INVALID_TOKEN renders the inline
 * banner with a link back to forgot-password.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const headingRef = useRef<HTMLDivElement>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [showInvalidTokenLink, setShowInvalidTokenLink] = useState(false);

  useEffect(() => {
    if (!token && headingRef.current) headingRef.current.focus();
  }, [token]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PasswordSetInput>({
    resolver: zodResolver(passwordSetSchema),
    mode: 'onSubmit',
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  if (!token) {
    return (
      <CardChrome>
        <div ref={headingRef} tabIndex={-1}>
          <h1 className="text-lg font-semibold text-gray-900 text-center mb-4">
            Link unavailable
          </h1>
          <p className="text-sm text-gray-600 text-center mb-4">
            This link is invalid or has expired.
          </p>
          <Link
            to="/external/forgot-password"
            className="block text-center text-sm text-primary-600 hover:text-primary-700"
          >
            Request a new reset link
          </Link>
        </div>
      </CardChrome>
    );
  }

  const onSubmit = async (values: PasswordSetInput) => {
    setBannerError(null);
    setShowInvalidTokenLink(false);
    try {
      await resetPassword({ token, newPassword: values.newPassword });
      navigate('/external/login', {
        state: { message: 'Password reset — sign in below.' },
      });
    } catch (err) {
      if (err instanceof ExternalApiError && err.errorCode === 'INVALID_TOKEN') {
        setBannerError('This link is invalid or has expired.');
        setShowInvalidTokenLink(true);
      } else {
        setBannerError('Something went wrong, please try again.');
      }
    }
  };

  return (
    <CardChrome>
      {bannerError && (
        <div
          role="alert"
          className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4"
        >
          {bannerError}
          {showInvalidTokenLink && (
            <>
              {' '}
              <Link to="/external/forgot-password" className="underline">
                Request a new reset link
              </Link>
            </>
          )}
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          error={errors.newPassword?.message}
          {...register('newPassword')}
        />
        <div className="mt-4">
          <Input
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />
        </div>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          isLoading={isSubmitting}
          className="w-full mt-6"
        >
          Set new password
        </Button>
      </form>
    </CardChrome>
  );
}
